var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { resourcesService } from 'ziphy-web-shared/basic/api';
import { areStringArraysEqual, getUuid } from 'ziphy-web-shared/basic/helpers';
import { useInnerValue } from 'ziphy-web-shared/basic/hooks';
import { useDropdown, } from 'ziphy-web-shared/basic/lib/functional';
import { $modal } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { checkIfSearchable, createCodeableConcept, getFhirRef, getResourceId, getResourceProperties, } from './';
import SearchResourceDropdownItem from './lib/SearchResourceDropdownItem';
function getSelectedIds(fhirRef) {
    return (Array.isArray(fhirRef) ? fhirRef : typeof fhirRef === 'string' && fhirRef ? [fhirRef] : []).map((x) => getResourceId(x));
}
export const useResourceSearchDropdown = ({ encounterId, resourceContext, practiceSettings, onSelectSearchResult, onSelectResource, initialSelected: _initialSelected, initialSearchString, isMulti, isEdit, }) => {
    const [isAddingNew, setIsAddingNew] = useState(false);
    const contentMountEventName = useMemo(() => getUuid(), []);
    const initialSelected = useMemo(() => getSelectedIds(_initialSelected), [_initialSelected]);
    const [selectedIds, setSelectedIds] = useInnerValue(initialSelected);
    const handleSelectResource = useCallback((resourceId) => __awaiter(void 0, void 0, void 0, function* () {
        if (isMulti) {
            setSelectedIds((prev) => {
                if (prev.includes(resourceId)) {
                    return prev.filter((item) => item !== resourceId);
                }
                else {
                    return [...prev, resourceId];
                }
            });
        }
        else {
            onSelectResource(getFhirRef(resourceContext.resourceType, resourceId));
        }
    }), [onSelectResource]);
    const openResourceModal = (resourceId, searchTaxonomy) => {
        const properties = getResourceProperties(resourceContext);
        /*
         * todo: KosTbIJlb detected
         *
         *       ||
         *       ||
         *       ||
         *      _||_
         *     |____|
         *     | || |
         *     | || |
         *     | || |
         *     | || |
         *     | || |
         *     |_||_|
         *      |__|
         *     /____\
         *
         * */
        if (resourceContext.element === 'condition.chief_complaint') {
            resourceContext = _.cloneDeep(resourceContext);
            if (resourceId) {
                if (!selectedIds.includes(resourceId))
                    resourceContext.element = 'condition';
            }
            else if (selectedIds.length > 0) {
                resourceContext.element = 'condition';
            }
        }
        // todo: KosTbIJlb detected
        $modal.add(properties.ModalComponent, {
            encounterId,
            resourceId,
            resourceContext,
            searchTaxonomy,
            practiceSettings,
            onEnter: () => setIsAddingNew(true),
            onLeaved: () => setIsAddingNew(false),
            onDone: (props) => __awaiter(void 0, void 0, void 0, function* () {
                if (onSelectResource) {
                    yield resourceSearchDropdown.reloadAsyncItems();
                    if (!isMulti || !selectedIds.includes(resourceId)) {
                        yield handleSelectResource(props.resourceId);
                    }
                }
            }),
        });
    };
    const handleSearch = useCallback((text) => __awaiter(void 0, void 0, void 0, function* () {
        return yield resourcesService.search({
            encounterId,
            element: resourceContext.element,
            searchPreference: resourceContext.searchPreference,
            text: text,
        });
    }), [resourceContext === null || resourceContext === void 0 ? void 0 : resourceContext.element, practiceSettings]);
    const resourceSearchDropdown = useDropdown([], {
        initialSearchString,
        onSearch: useCallback((searchString, state) => __awaiter(void 0, void 0, void 0, function* () {
            if (!resourceContext ||
                !checkIfSearchable(resourceContext.searchPreference) ||
                (state === null || state === void 0 ? void 0 : state.isOpen) === null ||
                (state === null || state === void 0 ? void 0 : state.isOpen) === false) {
                return;
            }
            const response = yield handleSearch(searchString);
            if (!response.error && response.prepared)
                return response.prepared;
        }), [encounterId, resourceContext === null || resourceContext === void 0 ? void 0 : resourceContext.element]),
        onSearchRender: useCallback((searchedData) => {
            let items = [];
            if (!searchedData || !resourceContext)
                return items;
            const properties = getResourceProperties(resourceContext);
            Object.entries(searchedData).forEach(([key, values]) => {
                items.push({
                    id: key,
                    label: properties.searchLabels[key] || key,
                    items: values.map((item) => {
                        const isExisting = 'id' in item;
                        const isSelected = isExisting ? selectedIds === null || selectedIds === void 0 ? void 0 : selectedIds.includes(item.id) : false;
                        return {
                            id: isExisting ? item.id : [item.system, item.code].join('_'),
                            label: (_jsx(SearchResourceDropdownItem, { item: item, resourceContext: resourceContext, onClick: onSelectResource && isExisting && isSelected
                                    ? (event) => {
                                        event.stopPropagation();
                                        openResourceModal(item.id);
                                    }
                                    : undefined })),
                            selected: isSelected,
                            onClick: (event) => __awaiter(void 0, void 0, void 0, function* () {
                                if (onSelectSearchResult) {
                                    yield onSelectSearchResult(item);
                                }
                                if (onSelectResource) {
                                    if (isExisting) {
                                        if ((event === null || event === void 0 ? void 0 : event.ctrlKey) || (event === null || event === void 0 ? void 0 : event.metaKey)) {
                                            openResourceModal(item.id);
                                            return;
                                        }
                                        return yield handleSelectResource(item.id);
                                    }
                                    if (properties.silentCreate) {
                                        const response = yield properties.service.create({
                                            encounterId,
                                            elementName: resourceContext.element,
                                            data: { code: createCodeableConcept(item) },
                                        });
                                        if (!response.error)
                                            yield handleSelectResource(response.prepared.id);
                                    }
                                    else {
                                        openResourceModal('', item);
                                    }
                                }
                            }),
                        };
                    }),
                });
            });
            return items;
        }, [resourceContext === null || resourceContext === void 0 ? void 0 : resourceContext.element, selectedIds, onSelectSearchResult, onSelectResource]),
    });
    useEffect(() => {
        if (contentMountEventName) {
            const callback = (e) => {
                const { mounted } = e.detail;
                if (mounted) {
                    setSelectedIds(initialSelected);
                }
            };
            window.addEventListener(contentMountEventName, callback, false);
            return () => window.removeEventListener(contentMountEventName, callback, false);
        }
    }, [contentMountEventName, initialSelected]);
    const selectedItems = selectedIds;
    const handleOnReset = () => setSelectedIds(initialSelected);
    const handleOnRemoveAll = () => setSelectedIds([]);
    const handleOnRemove = (id) => setSelectedIds((p) => p.filter((x) => x !== id));
    const handleOnDone = (onClose) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        let ids = selectedIds;
        let idsMap;
        if (((_a = resourceSearchDropdown.searchString) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            const response = yield handleSearch();
            if (!response.error && response.prepared) {
                idsMap = new Set(response.prepared.resources.map((x) => x.id));
            }
        }
        else {
            idsMap = new Set(resourceSearchDropdown.filteredItems.flatMap((x) => x.items).map((x) => x.id));
        }
        ids = ids.filter((id) => idsMap.has(id));
        if (isEdit && areStringArraysEqual(initialSelected, ids)) {
            (_b = resourceSearchDropdown.dropdownRef.current) === null || _b === void 0 ? void 0 : _b.setIsOpen(false);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
        else {
            yield onSelectResource(ids.map((x) => getFhirRef(resourceContext.resourceType, x)));
        }
    });
    return {
        resourceSearchDropdown,
        isAddingNew,
        setIsAddingNew,
        openResourceModal,
        //
        selectedIds,
        selectedItems,
        handleOnDone,
        handleOnReset,
        handleOnRemoveAll,
        handleOnRemove,
        contentMountEventName,
    };
};
